import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, ShieldCheck, Link2Off } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Logo, LogoMark } from "@/components/Logo";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, fmtDate } from "@/lib/format";

const REASONS = [
  { key: "amount", label: "Amount" },
  { key: "date", label: "Date" },
  { key: "unknown", label: "I don't recognise this" },
  { key: "other", label: "Something else" },
];

export default function PublicRepayment() {
  const { token } = useParams();
  const [view, setView] = useState("main"); // main | question | confirmed | questioned
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["repay", token],
    queryFn: async () => (await api.get(`/r/${token}`)).data,
    retry: false,
  });

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/r/${token}/confirm`);
      setView("confirmed");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuestion = async () => {
    if (!reason) return toast.error("Please choose what looks incorrect.");
    setSubmitting(true);
    try {
      await api.post(`/r/${token}/question`, { reason, comment: comment || null });
      setView("questioned");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    const code = apiError(error);
    return (
      <PublicWrap>
        <div className="text-center py-10">
          <Link2Off className="mx-auto h-14 w-14 text-muted-foreground" />
          <h1 className="mt-4 font-display text-xl font-bold">
            {code === "expired_link" ? "This link has expired" : "This link isn't valid"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">Please ask the person who sent it to share a fresh link.</p>
        </div>
      </PublicWrap>
    );
  }

  if (isLoading || !data) {
    return (
      <PublicWrap>
        <div className="flex justify-center py-16"><div className="animate-pop"><LogoMark size={56} /></div></div>
      </PublicWrap>
    );
  }

  if (view === "confirmed" || (data.already_responded && data.status === "confirmed" && view === "main")) {
    return <ThankYou icon={CheckCircle2} title="Confirmed ✓" subtitle="Thanks — your confirmation has been recorded." />;
  }
  if (view === "questioned" || (data.already_responded && data.status === "questioned" && view === "main")) {
    return <ThankYou icon={AlertTriangle} title="Response recorded" subtitle="We've let them know this repayment is being questioned." tone="amber" />;
  }

  const iOwe = data.direction === "i_owe";

  if (view === "question") {
    return (
      <PublicWrap>
        <h1 className="font-display text-xl font-bold text-foreground">What looks incorrect?</h1>
        <div className="mt-4 space-y-2">
          {REASONS.map((r) => (
            <button
              key={r.key}
              data-testid={`repay-reason-${r.key}`}
              onClick={() => setReason(r.key)}
              className={`w-full text-left rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${reason === r.key ? "border-primary bg-secondary text-primary" : "border-border bg-white text-foreground"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <Textarea
          data-testid="repay-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a short comment (optional)"
          className="mt-3 rounded-xl resize-none"
          rows={3}
        />
        <Button data-testid="repay-question-submit" onClick={submitQuestion} disabled={submitting} className="mt-4 w-full h-12 rounded-xl">
          {submitting ? "Submitting…" : "Submit"}
        </Button>
        <Button variant="ghost" onClick={() => setView("main")} className="mt-2 w-full h-10 rounded-xl">Back</Button>
      </PublicWrap>
    );
  }

  return (
    <PublicWrap>
      <p className="text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">{data.person_name}</span>,{" "}
        {iOwe
          ? <>{data.payer_name} says they've repaid you and asked you to confirm you received it.</>
          : <>{data.payer_name} recorded a repayment from you and asked you to confirm it's correct.</>}
      </p>

      {data.revised && (
        <div className="mt-3 flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 p-3">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900">This was updated after you questioned it. Please review the latest version.</p>
        </div>
      )}

      <div className="mt-5 rounded-3xl bg-card border border-border p-5">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">Repayment amount</p>
        <p className="font-display text-4xl font-extrabold tabular text-foreground">{fmtNaira(data.amount, data.currency)}</p>
        <div className="mt-4 divide-y divide-border">
          <Row label={iOwe ? "Paid by" : "Recorded by"} value={data.payer_name} />
          <Row label="Date" value={fmtDate(data.payment_date)} />
          <Row label="Remaining balance" value={fmtNaira(data.outstanding_amount, data.currency)} />
        </div>
      </div>

      <div className="mt-5 space-y-2.5">
        <Button data-testid="public-confirm-button" onClick={confirm} disabled={submitting} className="w-full h-12 rounded-xl text-base">
          <CheckCircle2 className="h-5 w-5" /> {iOwe ? "Yes, I received it" : "Yes, that's correct"}
        </Button>
        <Button data-testid="public-question-button" variant="outline" onClick={() => setView("question")} className="w-full h-12 rounded-xl">
          Something isn't correct
        </Button>
      </div>

      <div className="mt-5 flex items-start gap-2 rounded-2xl bg-secondary p-3">
        <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">
          Confirming records that you agree this repayment happened. Debii does not move money or guarantee repayment.
        </p>
      </div>
    </PublicWrap>
  );
}

function PublicWrap({ children }) {
  return (
    <div className="debii-shell min-h-screen px-6 py-8">
      <div className="flex justify-center"><Logo size={30} /></div>
      <div className="mt-8 max-w-sm mx-auto w-full animate-rise">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground text-right">{value}</span>
    </div>
  );
}

function ThankYou({ icon: Icon, title, subtitle, tone = "green" }) {
  return (
    <PublicWrap>
      <div className="text-center py-6">
        <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center animate-pop ${tone === "amber" ? "bg-amber-100" : "bg-emerald-100"}`}>
          <Icon className={`h-11 w-11 ${tone === "amber" ? "text-amber-700" : "text-emerald-700"}`} />
        </div>
        <h1 className="mt-5 font-display text-2xl font-bold text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>

        <div className="mt-10 rounded-3xl bg-primary text-primary-foreground p-6">
          <p className="font-display text-lg font-bold">Keep track of money people owe you too.</p>
          <p className="mt-1 text-sm opacity-80">Debii makes it simple to remember who owes you and when.</p>
          <Button
            data-testid="repay-try-debii"
            onClick={() => (window.location.href = "/welcome")}
            className="mt-4 w-full h-12 rounded-xl bg-white text-primary hover:bg-white/90 text-base"
          >
            Try Debii Free
          </Button>
        </div>
      </div>
    </PublicWrap>
  );
}
